<template lang="pug">
.register-row
  .register-group
    .register-key
      span.key-name {{ keyName }}
      span.validation-label(v-if="required == true") 必須
    .register-key
      slot(name="optionText")

    .register-value
      .multi-select-dropdown(style="width: 380px;")
        b-dropdown.w-100(
          variant="outline-secondary"
          menu-class="w-100 scrollable-menu"
          toggle-class="d-flex justify-content-between align-items-center custom-select-toggle"
          :no-flip="noFlip"
        )
          template(#button-content)
            span.selected-label.text-left.flex-grow-1.ml-2(:class="{ 'placeholder-color': _selectedCount === 0 }") {{ _displayLabel }}
          b-dropdown-form(@click.native.stop)
            slot(name="filters")
          b-dropdown-divider(v-if="$slots.filters")
          .options-list.p-2(@click.native.stop)
            b-form-checkbox.mb-2(
              v-for="option in options"
              v-model="_value"
              :key="option.value"
              :value="option.value"
              @click.native.stop
            ) {{ option.label }}

    .error-messages(v-for="error in errors.collect(name)")
      label.error-message {{ error }}
      br
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";

/**
 * 入力要素コンポーネント(複数選択)
 *
 * - セレクトボックスの入力をサポートする
 * - 既存の入力画面のスタイルが適用される
 * - バリデーターは親コンポーネントのものをInjectionして使用する
 *   - そのため、親コンポーネントでveevalidateをnewしておく必要がある
 */
@Component
export default class FormInputMultiSelect extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 親コンポーネントでv-modelで指定した値 */
  @Prop({ required: true })
  value!: number[] | string[];

  /** リスト項目 */
  @Prop()
  options!: Array<{ value: number | string; label: string }>;

  /** 項目名 */
  @Prop({ required: true, type: String })
  keyName!: string;

  /** 必須か */
  @Prop({
    default: () => {
      return false;
    },
    type: Boolean
  })
  required!: boolean;

  /** name属性の値 */
  @Prop({ required: true, type: String })
  name!: string;

  /** veevalidateに渡すバリデーション項目名 */
  @Prop({
    default: () => {
      return "";
    },
    type: String
  })
  validationKeyName!: string;

  /** veevalidateに渡すバリデーションルール */
  @Prop()
  validationRules!: string;

  /** 横幅 */
  @Prop({ default: 380 })
  width!: number;

  /** 上方向への自動反転（dropup）を防止して常に下に表示するか */
  @Prop({
    default: true,
    type: Boolean
  })
  noFlip!: boolean;

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _value(): number[] | string[] {
    return this.value;
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _value(newValue: number[] | string[]) {
    this.$emit("input", newValue);
    return;
  }

  get _selectedCount(): number {
    return Array.isArray(this.value) ? this.value.length : 0;
  }

  get _displayLabel(): string {
    if (this._selectedCount === 0) {
      return `${this.keyName}を選択してください`;
    }
    if (this._selectedCount === 1) {
      const found = this.options.find((opt) => opt.value === this.value[0]);
      return found ? found.label : `${this._selectedCount}件選択中`;
    }
    if (this.options.length > 0 && this._selectedCount === this.options.length) {
      return "全員選択中";
    }
    return `${this._selectedCount}件選択中`;
  }
}
</script>

<style lang="scss">
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.multi-select-dropdown {
  .scrollable-menu {
    max-height: 280px;
    overflow-y: auto;
  }

  .selected-label {
    font-size: 16px;
    &.placeholder-color {
      color: #6c757d !important;
    }
  }

  .custom-control-label {
    font-size: 16px;
  }

  .search-filter-input-group {
    .search-filter-icon {
      background-color: #fff !important;
      .search-icon {
        @include svg-icon("../../../assets/search.svg", $font-base-color, 20px, 20px);
      }
    }
    .search-filter-input {
      border-left: 0;
    }
  }

  .custom-select-toggle {
    background-color: $bg-default !important;
    border: 1px solid $form-border-color !important;
    color: $font-base-color !important;
    height: 42px;
    padding: 6px 12px;
    box-shadow: none !important;

    &:hover,
    &:focus,
    &:active,
    &.show > .btn-secondary.dropdown-toggle {
      background-color: $bg-default !important;
      border-color: $form-border-color !important;
      color: $font-base-color !important;
    }

    &:focus,
    &.focus {
      outline: none;
      box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color !important;
      border: 1px solid $form-border-select-color !important;
    }

    &::after {
      margin-left: 10px;
    }
  }
}
</style>
